import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

import { CreditCard, Smartphone, Package } from 'lucide-react';
import { 
  sanitizeFormData, 
  sanitizeInput,
  validateEmail, 
  validatePhone, 
  validateZipCode,
  validateCardNumber,
  checkRateLimit
} from '../utils/security';
import { createOrderAndDecrementStock } from '../services/orderService';

const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const { formatMoney, getProductPrice, currency } = useCurrency();
  const { currentUser, userDoc, updateUser } = useAuth();
  const navigate = useNavigate();

  const CHECKOUT_DRAFT_KEY = 'ditris:checkoutDraft:v1';
  const pickDraftFields = (data) => {
    const src = data && typeof data === 'object' ? data : {};
    return {
      email: src.email || '',
      firstName: src.firstName || '',
      lastName: src.lastName || '',
      address: src.address || '',
      apartment: src.apartment || '',
      city: src.city || '',
      state: src.state || '',
      zipCode: src.zipCode || '',
      country: src.country || 'United States',
      phone: src.phone || '',
    };
  };

  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card', 'upi', 'cod'
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    phone: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    upiId: '',
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  const hydratedRef = useRef(false);

  // Load saved user information on mount
  useEffect(() => {
    if (hydratedRef.current) return;

    try {
      const raw = sessionStorage.getItem(CHECKOUT_DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setFormData((prevData) => ({
          ...prevData,
          ...pickDraftFields(parsed),
        }));
      }
    } catch {
      // ignore
    }

    const saved = userDoc?.profile;
    if (!saved || typeof saved !== 'object') {
      hydratedRef.current = true;
      return;
    }
    setFormData((prevData) => ({
      ...prevData,
      ...pickDraftFields(saved),
    }));
    hydratedRef.current = true;
  }, [userDoc?.profile]);

  useEffect(() => {
    try {
      const draft = pickDraftFields(formData);
      sessionStorage.setItem(CHECKOUT_DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // ignore
    }
  }, [
    formData.email,
    formData.firstName,
    formData.lastName,
    formData.address,
    formData.apartment,
    formData.city,
    formData.state,
    formData.zipCode,
    formData.country,
    formData.phone,
  ]);

  // Currency mismatch toast removed (too noisy during hydration and on refresh)

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const applyDiscount = () => {
    const raw = sanitizeInput(discountCode || '');
    const code = raw.trim().toUpperCase();
    if (!code) {
      setAppliedDiscount(null);
      return;
    }

    const known = {
      DITRIS10: { type: 'percent', value: 10 },
      WELCOME15: { type: 'percent', value: 15 },
      FREESHIP: { type: 'free_shipping', value: 0 },
    };

    const d = known[code];
    if (!d) {
      setAppliedDiscount(null);
      toast({ title: 'Invalid code', description: 'This discount code is not valid.' });
      return;
    }

    setAppliedDiscount({ code, ...d });
    toast({ title: 'Discount applied', description: `${code} applied.` });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // SECURITY: Rate limiting
    const rateCheck = checkRateLimit('checkout', 3, 60000); // 3 attempts per minute
    if (!rateCheck.allowed) {
      toast({
        title: "Too many attempts",
        description: "Please wait a moment before trying again.",
      });
      return;
    }
    
    // SECURITY: Validate inputs
    if (!validateEmail(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
      });
      return;
    }
    
    if (!validatePhone(formData.phone)) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number.",
      });
      return;
    }
    
    if (!validateZipCode(formData.zipCode)) {
      toast({
        title: "Invalid ZIP code",
        description: "Please enter a valid ZIP/postal code.",
      });
      return;
    }
    
    // Validate card if card payment selected
    if (paymentMethod === 'card' && !validateCardNumber(formData.cardNumber)) {
      toast({
        title: "Invalid card number",
        description: "Please enter a valid card number.",
      });
      return;
    }
    
    setIsProcessing(true);

    // SECURITY: Sanitize form data before saving
    const sanitizedData = sanitizeFormData(formData);
    
    // Save user information for future orders (excluding payment details)
    const userInfoToSave = {
      email: sanitizedData.email,
      firstName: sanitizedData.firstName,
      lastName: sanitizedData.lastName,
      address: sanitizedData.address,
      apartment: sanitizedData.apartment,
      city: sanitizedData.city,
      state: sanitizedData.state,
      zipCode: sanitizedData.zipCode,
      country: sanitizedData.country,
      phone: sanitizedData.phone,
    };
    
    if (typeof updateUser === 'function') {
      try {
        await updateUser({ profile: userInfoToSave, email: userInfoToSave.email });
      } catch {
        // ignore
      }
    }

    // Simulate payment processing
    setTimeout(async () => {
      try {
        // Create order in Firebase
        const orderData = {
          userId: currentUser?.uid,
          items: cart,
          total: total,
          totalDisplay,
          currency: currency,
          pricing: {
            subtotal,
            discount: discountAmount,
            shipping: shippingAmount,
            tax: taxAmount,
            total,
            subtotalDisplay,
            discountDisplay,
            shippingDisplay,
            taxDisplay,
            totalDisplay,
            discountCode: appliedDiscount?.code || '',
          },
          shippingAddress: {
            firstName: sanitizedData.firstName,
            lastName: sanitizedData.lastName,
            address: sanitizedData.address,
            apartment: sanitizedData.apartment,
            city: sanitizedData.city,
            state: sanitizedData.state,
            zipCode: sanitizedData.zipCode,
            country: sanitizedData.country,
            phone: sanitizedData.phone,
          },
          email: sanitizedData.email,
          paymentMethod: paymentMethod,
          status: 'pending'
        };

        const created = await createOrderAndDecrementStock(orderData);
        
        setIsProcessing(false);
        clearCart();
        
        let successMessage = "Thank you for your order. You will receive a confirmation email shortly.";
        if (paymentMethod === 'upi') {
          successMessage = "UPI payment request sent. Please complete the payment in your UPI app.";
        } else if (paymentMethod === 'cod') {
          successMessage = "Order confirmed! You can pay when the order is delivered.";
        }
        
        toast({
          title: "Order placed successfully!",
          description: successMessage,
        });

        try {
          sessionStorage.removeItem(CHECKOUT_DRAFT_KEY);
        } catch {
          // ignore
        }

        navigate('/order-confirmation', {
          state: {
            orderId: created?.id,
            orderNumber: created?.orderNumber,
          },
        });
      } catch (error) {
        console.error('Error creating order:', error);
        toast({
          title: 'Order failed',
          description: String(error?.message || 'Please try again.'),
        });
        setIsProcessing(false);
      }
    }, 2000);
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <h1 className="text-2xl font-medium tracking-wide mb-4">Your cart is empty</h1>
        <button
          onClick={() => navigate('/shop/new-arrivals')}
          className="text-sm underline hover:opacity-60"
        >
          Continue shopping
        </button>
      </div>
    );
  }

  const subtotal = getCartTotal();

  const discountAmount = (() => {
    if (!appliedDiscount) return 0;
    if (appliedDiscount.type === 'percent') {
      const pct = Number(appliedDiscount.value || 0) / 100;
      return Math.max(0, (Number(subtotal) || 0) * pct);
    }
    if (appliedDiscount.type === 'amount') {
      return Math.max(0, Number(appliedDiscount.value || 0));
    }
    return 0;
  })();

  const discountedSubtotal = Math.max(0, (Number(subtotal) || 0) - discountAmount);

  const shippingAmount = (() => {
    if (appliedDiscount?.type === 'free_shipping') return 0;

    const tierFreeThreshold = 200;
    if (discountedSubtotal >= tierFreeThreshold) return 0;

    const country = String(formData.country || '').trim();
    const base = ['United States', 'Canada'].includes(country) ? 15 : 25;
    if (discountedSubtotal >= 100) return Math.max(0, base - 5);
    return base;
  })();

  const taxRate = (() => {
    const country = String(formData.country || '').trim();
    if (country === 'United Kingdom') return 0.2;
    if (country === 'Canada') return 0.13;
    if (country === 'India') return 0.18;
    if (country === 'Australia') return 0.1;
    if (country === 'Japan') return 0.1;
    if (['Germany', 'France', 'Spain', 'Italy'].includes(country)) return 0.19;
    return 0.08;
  })();

  const taxAmount = discountedSubtotal * taxRate;
  const total = discountedSubtotal + shippingAmount + taxAmount;

  const discountDisplay = discountAmount;
  const shippingDisplay = shippingAmount;
  const taxDisplay = taxAmount;
  const subtotalDisplay = subtotal;
  const totalDisplay = total;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-medium tracking-wide mb-8">CHECKOUT</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Contact Information */}
          <div>
            <h2 className="text-lg font-medium mb-4">CONTACT INFORMATION</h2>
            <input
              type="email"
              name="email"
              placeholder="Email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
            />
          </div>

          {/* Shipping Address */}
          <div>
            <h2 className="text-lg font-medium mb-4">SHIPPING ADDRESS</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  name="firstName"
                  placeholder="First name"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                />
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last name"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                />
              </div>
              <input
                type="text"
                name="address"
                placeholder="Address"
                required
                value={formData.address}
                onChange={handleChange}
                className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
              />
              <input
                type="text"
                name="apartment"
                placeholder="Apartment, suite, etc. (optional)"
                value={formData.apartment}
                onChange={handleChange}
                className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
              />
              <div className="grid grid-cols-3 gap-4">
                <input
                  type="text"
                  name="city"
                  placeholder="City"
                  required
                  value={formData.city}
                  onChange={handleChange}
                  className="border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                />
                <input
                  type="text"
                  name="state"
                  placeholder="State"
                  required
                  value={formData.state}
                  onChange={handleChange}
                  className="border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                />
                <input
                  type="text"
                  name="zipCode"
                  placeholder="ZIP code"
                  required
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                />
              </div>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white"
              >
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="India">India</option>
                <option value="Australia">Australia</option>
                <option value="Japan">Japan</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="Spain">Spain</option>
                <option value="Italy">Italy</option>
                <option value="Other">Other</option>
              </select>
              <input
                type="tel"
                name="phone"
                placeholder="Phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
              />
            </div>
          </div>

          {/* Payment */}
          <div>
            <h2 className="text-lg font-medium mb-4">PAYMENT METHOD</h2>
            
            {/* Payment Method Selection */}
            <div className="space-y-3 mb-6">
              <button
                type="button"
                onClick={() => setPaymentMethod('card')}
                className={`w-full flex items-center gap-3 p-4 border-2 transition-colors ${
                  paymentMethod === 'card'
                    ? 'border-black bg-gray-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <CreditCard size={24} />
                <div className="text-left">
                  <div className="font-medium">Credit/Debit Card</div>
                  <div className="text-xs text-gray-500">Pay securely with your card</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setPaymentMethod('upi')}
                className={`w-full flex items-center gap-3 p-4 border-2 transition-colors ${
                  paymentMethod === 'upi'
                    ? 'border-black bg-gray-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <Smartphone size={24} />
                <div className="text-left">
                  <div className="font-medium">UPI</div>
                  <div className="text-xs text-gray-500">PhonePe, Google Pay, Paytm & more</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setPaymentMethod('cod')}
                className={`w-full flex items-center gap-3 p-4 border-2 transition-colors ${
                  paymentMethod === 'cod'
                    ? 'border-black bg-gray-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <Package size={24} />
                <div className="text-left">
                  <div className="font-medium">Cash on Delivery</div>
                  <div className="text-xs text-gray-500">Pay when you receive your order</div>
                </div>
              </button>
            </div>

            {/* Payment Details Form */}
            {paymentMethod === 'card' && (
              <div className="space-y-4">
                <input
                  type="text"
                  name="cardNumber"
                  placeholder="Card number"
                  required
                  value={formData.cardNumber}
                  onChange={handleChange}
                  className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="expiryDate"
                    placeholder="MM / YY"
                    required
                    value={formData.expiryDate}
                    onChange={handleChange}
                    className="border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                  />
                  <input
                    type="text"
                    name="cvv"
                    placeholder="CVV"
                    required
                    value={formData.cvv}
                    onChange={handleChange}
                    className="border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                  />
                </div>
              </div>
            )}

            {paymentMethod === 'upi' && (
              <div>
                <input
                  type="text"
                  name="upiId"
                  placeholder="Enter UPI ID (e.g., yourname@paytm)"
                  required={paymentMethod === 'upi'}
                  value={formData.upiId}
                  onChange={handleChange}
                  className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                />
                <p className="text-xs text-gray-500 mt-2">
                  You will receive a payment request on your UPI app
                </p>
              </div>
            )}

            {paymentMethod === 'cod' && (
              <div className="bg-amber-50 border border-amber-200 p-4 rounded">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Cash on Delivery is available for orders within India. 
                  Please keep exact change ready when the delivery arrives.
                </p>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isProcessing || subtotal === null}
            className="w-full bg-black text-white py-4 text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors disabled:bg-gray-400"
          >
            {isProcessing ? 'PROCESSING...' : 'PLACE ORDER'}
          </button>
          {subtotal === null ? (
            <p className="text-xs text-red-600 mt-3">
              One or more items are not available in {currency}. Please change your region or remove the item.
            </p>
          ) : null}
        </form>

        {/* Order Summary */}
        <div>
          <div className="bg-gray-50 p-6 sticky top-24">
            <h2 className="text-lg font-medium mb-6">ORDER SUMMARY</h2>
            
            {/* Cart Items */}
            <div className="space-y-4 mb-6">
              {cart.map((item, index) => (
                <div key={`${item.id}-${item.size}-${item.color}-${index}`} className="flex gap-4">
                  <img
                    src={item.images?.[0]}
                    alt={item.name}
                    className="w-20 h-20 object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium mb-1">{item.name}</h3>
                    <p className="text-xs text-gray-500 mb-1">
                      Size: {item.size} / Color: {item.color}
                    </p>
                    <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                  </div>
                  <p className="text-sm font-medium">
                    {formatMoney((getProductPrice(item) ?? null) === null ? null : Number(getProductPrice(item)) * Number(item.quantity || 0))}
                  </p>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-200 pt-4 pb-4">
              <div className="flex gap-3">
                <input
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  placeholder="Discount code"
                  className="flex-1 border border-gray-300 px-3 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white"
                />
                <button
                  type="button"
                  onClick={applyDiscount}
                  className="border border-black px-4 py-3 text-sm font-medium tracking-wide hover:bg-black hover:text-white transition-colors"
                >
                  APPLY
                </button>
              </div>
              {appliedDiscount?.code ? (
                <div className="mt-2 text-xs text-gray-600">Applied: {appliedDiscount.code}</div>
              ) : null}
            </div>

            {/* Totals */}
            <div className="border-t border-gray-200 pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>{formatMoney(subtotal)}</span>
              </div>
              {discountAmount > 0 ? (
                <div className="flex justify-between text-sm text-green-700">
                  <span>Discount</span>
                  <span>-{formatMoney(discountDisplay)}</span>
                </div>
              ) : null}
              <div className="flex justify-between text-sm">
                <span>Shipping</span>
                <span>{shippingAmount === 0 ? 'Free' : formatMoney(shippingDisplay)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Estimated tax</span>
                <span>{formatMoney(taxDisplay)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium pt-2 border-t border-gray-200">
                <span>Total</span>
                <span>{formatMoney(totalDisplay)}</span>
              </div>
            </div>

            {shippingAmount > 0 && (
              <p className="text-xs text-gray-500 mt-4">
                Free shipping on orders over {formatMoney(200)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;